/**
 * Student Feedback Dashboard Component
 *
 * Admin dashboard for viewing and managing student feedback submissions
 */

import React, { useState, useEffect, useMemo } from 'react';
import { advisorApiService } from '../services/advisor-api';
import type { AdminFeedbackResponse, StudentFeedbackRecord } from '../types';
import '../styles/StudentFeedbackDashboard.css';

type SortOrder = 'asc' | 'desc';

interface StudentFeedbackDashboardProps {
  onFeedbackClick?: (feedback: StudentFeedbackRecord) => void;
}

export const StudentFeedbackDashboard: React.FC<StudentFeedbackDashboardProps> = ({
  onFeedbackClick,
}) => {
  const [feedbackData, setFeedbackData] = useState<AdminFeedbackResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(100);
  const [inputLimit, setInputLimit] = useState('100');

  // Sorting and filtering state
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [actionedFilter, setActionedFilter] = useState<string>('all');
  const [showActionedFilter, setShowActionedFilter] = useState(false);

  const fetchFeedbackData = async (feedbackLimit: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await advisorApiService.getAdminFeedbackData(feedbackLimit);
      if (response.data) {
        setFeedbackData(response.data);
      } else {
        setError(response.error || 'Failed to fetch feedback data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbackData(limit);
  }, [limit]);

  const handleLimitChange = () => {
    const newLimit = parseInt(inputLimit, 10);
    if (newLimit >= 1 && newLimit <= 1000) {
      setLimit(newLimit);
      fetchFeedbackData(newLimit);
    } else {
      alert('Limit must be between 1 and 1000');
    }
  };

  // Apply sorting and filtering
  const filteredAndSortedFeedbacks = useMemo(() => {
    if (!feedbackData) return [];

    let filtered = [...feedbackData.feedbacks];

    // Apply actioned filter
    if (actionedFilter !== 'all') {
      const isActioned = actionedFilter === 'Yes';
      filtered = filtered.filter((f) => f.is_actioned === isActioned);
    }

    // Sort by created_time
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_time).getTime();
      const dateB = new Date(b.created_time).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return filtered;
  }, [feedbackData, actionedFilter, sortOrder]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const toggleSort = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const handleFeedbackRowClick = (feedback: StudentFeedbackRecord) => {
    if (onFeedbackClick) {
      onFeedbackClick(feedback);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowActionedFilter(false);
    };
    if (showActionedFilter) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showActionedFilter]);

  if (isLoading) {
    return <div className="feedback-loading">Loading feedback data...</div>;
  }

  if (error) {
    return (
      <div className="error-box-wrap">
        <div className="error-box">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!feedbackData) {
    return (
      <div className="error-box-wrap">
        <div className="error-box">
          <h3>Error</h3>
          <p>No data available</p>
        </div>
      </div>
    );
  }

  const actionedPercentage =
    feedbackData.totalFeedbacks > 0
      ? ((feedbackData.actionedFeedbacks / feedbackData.totalFeedbacks) * 100).toFixed(1)
      : '0.0';

  return (
    <div className="feedback-dashboard">
      <div className="feedback-header">
        <h2>Student Support</h2>
        <div className="feedback-controls">
          <label>
            Results:
            <input
              type="number"
              value={inputLimit}
              onChange={(e) => setInputLimit(e.target.value)}
              min="1"
              max="1000"
            />
          </label>
          <button onClick={handleLimitChange}>Apply</button>
          <button onClick={() => fetchFeedbackData(limit)}>Refresh</button>
        </div>
      </div>

      <div className="feedback-statistics">
        <div className="stat-card">
          <div className="stat-value">{feedbackData.totalFeedbacks}</div>
          <div className="stat-label">Total Feedbacks</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{feedbackData.actionedFeedbacks}</div>
          <div className="stat-label">Actioned Feedbacks</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{actionedPercentage}%</div>
          <div className="stat-label">Actioned Percentage</div>
        </div>
      </div>

      <div className="feedback-table-container">
        <h2 className="feedback-table-title">
          Recent Feedbacks (Showing {filteredAndSortedFeedbacks.length} of {feedbackData.totalFeedbacks})
        </h2>
        <table className="feedback-table">
          <thead>
            <tr>
              <th className="fb-sortable-header" onClick={toggleSort}>
                <div className="fb-header-content">
                  <span>Created</span>
                  <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                </div>
              </th>
              <th>Name</th>
              <th>UNI</th>
              <th>Email</th>
              <th>Advising Email</th>
              <th>Feedback</th>
              <th>Phone Number</th>
              <th className="filter-header">
                <div className="fb-header-content">
                  <span>Is Actioned</span>
                  <button
                    className="filter-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActionedFilter(!showActionedFilter);
                    }}
                  >
                    ⚙
                  </button>
                </div>
                {showActionedFilter && (
                  <div className="fb-filter-dropdown" onClick={(e) => e.stopPropagation()}>
                    <div
                      className={`fb-filter-option ${actionedFilter === 'all' ? 'active' : ''}`}
                      onClick={() => {
                        setActionedFilter('all');
                        setShowActionedFilter(false);
                      }}
                    >
                      All
                    </div>
                    <div
                      className={`fb-filter-option ${actionedFilter === 'Yes' ? 'active' : ''}`}
                      onClick={() => {
                        setActionedFilter('Yes');
                        setShowActionedFilter(false);
                      }}
                    >
                      Yes
                    </div>
                    <div
                      className={`fb-filter-option ${actionedFilter === 'No' ? 'active' : ''}`}
                      onClick={() => {
                        setActionedFilter('No');
                        setShowActionedFilter(false);
                      }}
                    >
                      No
                    </div>
                  </div>
                )}
              </th>
              <th>Admin Comment</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedFeedbacks.length === 0 ? (
              <tr>
                <td colSpan={9} className="no-data">
                  No feedback records found
                </td>
              </tr>
            ) : (
              filteredAndSortedFeedbacks.map((feedback) => (
                <tr
                  key={feedback.id}
                  className="feedback-row"
                  onClick={() => handleFeedbackRowClick(feedback)}
                >
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(feedback.created_time)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{feedback.name}</td>
                  <td>{feedback.uni}</td>
                  <td>{feedback.email}</td>
                  <td>{feedback.advising_email}</td>
                  <td className="feedback-text">
                    <div className="feedback-preview">{feedback.feedback}</div>
                  </td>
                  <td>{feedback.phone_number || '-'}</td>
                  <td>
                    <span className={`actioned-badge ${feedback.is_actioned ? 'yes' : 'no'}`}>
                      {feedback.is_actioned ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="admin-comment-cell">{feedback.admin_comment || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="feedback-footer">
        Showing {filteredAndSortedFeedbacks.length} of {feedbackData.totalFeedbacks} total feedbacks
      </div>
    </div>
  );
};
